import React, { useState } from "react";
import Months from "./Months";
import { addMonths, clamp, daysBetween, monthStart, pickRange, rangeText, todayYmd } from "./calendar.mjs";
import "./datepicker.css";

/*
 * The inside of a date-range popover: ready-made ranges down the left, two
 * months to click a start and an end on (one on the phone), and the range so
 * far with Apply at the foot. Days are "YYYY-MM-DD", both ends included.
 *
 * A preset applies at once: its calendar would only be a picture of a choice
 * already made. `presets` items are { key, label, from, to, hint? } or carry
 * their own `onSelect` when they are not a plain pair of days (a rolling
 * "last 7 days" that runs to this minute, a page's own default).
 */
export default function RangePanel({
  from,
  to,
  min,
  max,
  presets = [],
  activeKey,
  onApply,
  onCancel,
  applyLabel = "Apply",
  note,
  mobile,
}) {
  const count = mobile ? 1 : 2;
  const today = todayYmd();
  const [sel, setSel] = useState({ from: from || null, to: to || null });
  const [hover, setHover] = useState(null);
  const [view, setView] = useState(() => monthStart(addMonths(clamp(to || from || today, min, max), -(count - 1))));

  const apply = () => sel.from && onApply({ from: sel.from, to: sel.to || sel.from });
  const lastDay = sel.to || (sel.from && hover && hover >= sel.from ? hover : null) || sel.from;

  return (
    <div className={`dp-range${mobile ? " is-phone" : ""}`}>
      {presets.length > 0 && (
        <div className="dp-presets" role="group" aria-label="Ready-made ranges">
          {presets.map((p) => {
            const on = activeKey ? activeKey === p.key : p.from === from && p.to === to;
            return (
              <button
                key={p.key}
                type="button"
                className={`dp-preset${on ? " is-on" : ""}`}
                aria-pressed={on}
                onClick={() => (p.onSelect ? p.onSelect() : onApply({ from: p.from, to: p.to }, p))}
              >
                {p.label}
                {p.hint && <span className="dp-preset__hint">{p.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
      <div className="dp-range__main">
        <Months
          count={count}
          view={view}
          onView={setView}
          from={sel.from}
          to={sel.to}
          hover={hover}
          min={min}
          max={max}
          onPick={(day) => setSel((s) => pickRange(s, day))}
          onHover={setHover}
        />
        {note && <p className="dp-note">{note}</p>}
        <div className="dp-range__foot">
          <span className="dp-range__sum" aria-live="polite">
            {!sel.from && "Click the first day"}
            {sel.from && (
              <>
                <b>{rangeText(sel.from, lastDay)}</b>
                {sel.to ? (
                  <span className="dp-faint"> · {daysBetween(sel.from, sel.to) + 1} {sel.from === sel.to ? "day" : "days"}</span>
                ) : (
                  <span className="dp-faint"> · now the last day</span>
                )}
              </>
            )}
          </span>
          <span className="dp-range__acts">
            <button type="button" className="crm-btn crm-btn--ghost dp-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="crm-btn crm-btn--dark dp-btn" disabled={!sel.from} onClick={apply}>
              {applyLabel}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
